/**
 * Supporting Files tab
 *
 * The scoped read half of the review page's Supporting Files tab: a
 * server-side table of the open submission's supporting files, restricted to
 * its deal and period. Deal and period are fixed by the caller (the review
 * shell), so — unlike the old standalone supporting-files page this is
 * ported from — there are no deal/period filter columns, and the query
 * cannot be widened past the selected month from here.
 *
 * Presentational: I/O is injected via props rather than a `useCases()` call,
 * because the review shell already owns a single `useCases()` call — see the
 * note in `./useCases.ts`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent, Loader, Modal, DialogFooter, Pagination } from "@/ui/atoms";
import { DataTable, EmptyState } from "@/ui/molecules";
import { Download, Pencil, Trash2, Upload } from "lucide-react";
import type { TSupportingFile, TPaginatedSupportingFiles, TAsyncState } from "@/store";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { buildRowCount } from "@/useCases/rowCount";
import { sortParam, textParam } from "@/useCases/serverTableQuery";

const PAGE_SIZE = 50;

export type TSupportingFilesTabProps = {
    /** Null for a standalone file with no deal — the tab renders its empty state. */
    dealId: string | null;
    /** `YYYY-MM-DD`; the tab scopes strictly to this month. */
    period: string;
    filesState: TAsyncState<TPaginatedSupportingFiles>;
    fetchData: (query: Record<string, unknown>, signal?: AbortSignal) => void;
    onDownload: (fileId: string) => void;
    onDelete: (fileId: string) => Promise<void>;
    canUpload: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canDownload: boolean;
    onRequestUpload: () => void;
    onRequestEdit: (file: TSupportingFile) => void;
    /**
     * Bumped by the shell after an upload or edit lands. Included in the
     * `useServerTable` refetch dependency so a completed modal refreshes the list
     * without the shell reaching into the tab's query state.
     */
    refreshKey: number;
};

function formatDateTime(isoString: string): string {
    return new Date(isoString).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/** Short label for content type (e.g. "PDF", "Image") */
function contentTypeLabel(contentType: string): string {
    if (!contentType) return "—";
    const lower = contentType.toLowerCase();
    if (lower.includes("pdf")) return "PDF";
    if (
        lower.includes("image") ||
        lower.includes("png") ||
        lower.includes("jpeg") ||
        lower.includes("gif")
    )
        return "Image";
    if (lower.includes("sheet") || lower.includes("excel") || lower.includes("csv"))
        return "Spreadsheet";
    if (lower.includes("word") || lower.includes("document")) return "Document";
    if (lower.includes("text")) return "Text";
    const parts = contentType.split("/");
    return parts.length > 1 ? parts[1]!.toUpperCase() : contentType;
}

const noopFetchData = (): void => {};

export function SupportingFilesTab(props: TSupportingFilesTabProps): React.ReactElement {
    const {
        dealId,
        period,
        filesState,
        fetchData,
        onDownload,
        onDelete,
        canUpload,
        canEdit,
        canDelete,
        canDownload,
        onRequestUpload,
        onRequestEdit,
        refreshKey,
    } = props;

    const [fileToDelete, setFileToDelete] = useState<TSupportingFile | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Deal and period come from the submission the page is showing, so they are
    // request scope rather than filters — the columns for them are gone, and the
    // user cannot widen past this month from here.
    const toQuery = useCallback(
        (input: TServerTableQueryInput) => ({
            skip: (input.page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            deal_id: dealId ?? undefined,
            period_from: period,
            period_to: period,
            related_file: textParam(input.columnFilters, "financialFileName"),
            friendly_name: textParam(input.columnFilters, "friendlyName"),
            original_filename: textParam(input.columnFilters, "originalFilename"),
            notes: textParam(input.columnFilters, "notes"),
            uploaded_by: textParam(input.columnFilters, "uploadedByName"),
            ...sortParam(input.sorting),
        }),
        [dealId, period],
    );

    // A deal-less file has no supporting files to list — issue no request rather
    // than fetching with a nonsensical deal_id.
    const server = useServerTable({
        pageSize: PAGE_SIZE,
        toQuery,
        fetchData: dealId === null ? noopFetchData : fetchData,
    });
    const { refetch, setPage } = server; // stable; used to refresh after a delete or a refreshKey bump

    // `useServerTable` returns to page 1 on a filter or sort change, but deal and
    // period are request scope here rather than filters, so they bypass that. A
    // month switch while past page 1 would otherwise re-request the new month at
    // the old `skip` and render it empty even when it has files. Resetting on
    // mount is a no-op (`page` starts at 1), so this needs no first-run guard.
    useEffect(() => {
        setPage(1);
    }, [dealId, period, setPage]);

    // Skip the very first run so mounting doesn't double-fetch alongside
    // useServerTable's own mount effect — only later bumps from the shell
    // (after an upload or edit lands) should force a refetch.
    const isFirstRefresh = useRef(true);
    useEffect(() => {
        if (isFirstRefresh.current) {
            isFirstRefresh.current = false;
            return;
        }
        refetch();
    }, [refreshKey, refetch]);

    const openDeleteModal = useCallback((file: TSupportingFile) => {
        setFileToDelete(file);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setIsDeleting((deleting) => {
            if (!deleting) setFileToDelete(null);
            return deleting;
        });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        const file = fileToDelete;
        if (!file) return;
        setIsDeleting(true);
        try {
            await onDelete(file.id);
            setFileToDelete(null);
            refetch(); // reflect the deletion without a page reload
        } finally {
            setIsDeleting(false);
        }
    }, [fileToDelete, onDelete, refetch]);

    const items = useMemo(() => filesState.data?.items ?? [], [filesState.data]);

    const columns: ColumnDef<TSupportingFile, unknown>[] = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                enableSorting: false,
                enableColumnFilter: false,
                cell: ({ row }) => (
                    <div className="flex items-center gap-1">
                        {canDownload && (
                            <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`supporting-file-download-${row.original.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDownload(row.original.id);
                                }}
                                title="Download"
                            >
                                <Download className="h-4 w-4" />
                            </Button>
                        )}
                        {canEdit && row.original.canEdit && (
                            <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`supporting-file-edit-${row.original.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRequestEdit(row.original);
                                }}
                                title="Edit"
                            >
                                <Pencil className="h-4 w-4" />
                            </Button>
                        )}
                        {canDelete && (
                            <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`supporting-file-delete-${row.original.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteModal(row.original);
                                }}
                                title="Delete"
                                className="text-destructive hover:text-destructive"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                ),
            },
            {
                id: "financialFileName",
                accessorKey: "financialFileName",
                header: "Related financial files",
                cell: ({ row }) => (
                    <span className="text-sm truncate max-w-[180px] block">
                        {row.original.financialFileName ?? "—"}
                    </span>
                ),
                meta: {
                    filterType: "text" as const,
                    filterPlaceholder: "Search related file...",
                },
            },
            {
                id: "friendlyName",
                accessorKey: "friendlyName",
                header: "File name",
                meta: {
                    filterType: "text" as const,
                    filterPlaceholder: "Search file name...",
                },
                cell: ({ row }) => (
                    <span className="text-sm truncate max-w-[160px] block">
                        {row.original.friendlyName}
                    </span>
                ),
            },
            {
                id: "originalFilename",
                accessorKey: "originalFilename",
                header: "Original file reference",
                cell: ({ row }) => (
                    <span className="text-sm truncate max-w-[180px] block">
                        {row.original.originalFilename}
                    </span>
                ),
                meta: {
                    filterType: "text" as const,
                    filterPlaceholder: "Search original filename...",
                },
            },
            {
                accessorKey: "contentType",
                header: "File type",
                cell: ({ row }) => contentTypeLabel(row.original.contentType),
                enableColumnFilter: false,
            },
            {
                id: "notes",
                accessorKey: "notes",
                header: "Notes",
                cell: ({ row }) => (
                    <span className="text-sm truncate max-w-[140px] block">
                        {row.original.notes ?? "—"}
                    </span>
                ),
                meta: { filterType: "text" as const, filterPlaceholder: "Filter by notes..." },
            },
            {
                accessorKey: "createdAt",
                header: "First upload",
                cell: ({ row }) => formatDateTime(row.original.createdAt),
                enableColumnFilter: false,
            },
            {
                id: "uploadedByName",
                accessorKey: "uploadedByName",
                header: "Originally uploaded by",
                cell: ({ row }) => row.original.uploadedByName ?? "—",
                meta: {
                    filterType: "text" as const,
                    filterPlaceholder: "Search uploader...",
                },
            },
            {
                accessorKey: "updatedAt",
                header: "Last updated",
                cell: ({ row }) => formatDateTime(row.original.updatedAt),
                enableColumnFilter: false,
            },
        ],
        [onDownload, onRequestEdit, openDeleteModal, canDownload, canDelete, canEdit],
    );

    const renderContent = () => {
        if (filesState.status === "pending" && !filesState.data) {
            return (
                <div className="flex justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        if (filesState.status === "rejected") {
            return (
                <EmptyState
                    variant="error"
                    title="Failed to load supporting files"
                    description={extractErrorMessage((filesState as { error: Error | null }).error)}
                />
            );
        }

        if (!items.length && server.columnFilters.length === 0) {
            return (
                <EmptyState
                    title="No supporting files yet"
                    description="Upload supporting documentation such as PDFs, images, or spreadsheets to accompany this submission."
                />
            );
        }

        return (
            <DataTable
                columns={columns}
                storageKey="supporting-files-tab"
                data={items}
                loading={filesState.status === "pending"}
                emptyMessage="No supporting files found."
                manualFiltering
                manualSorting
                sortingState={server.sorting}
                onSortingChange={server.onSortingChange}
                initialColumnFilters={server.columnFilters}
                onColumnFiltersChange={server.onColumnFiltersChange}
                rowNoun={{ one: "supporting file", many: "supporting files" }}
                rowCount={buildRowCount({
                    total: filesState.data?.total ?? 0,
                    totalUnfiltered: filesState.data?.totalUnfiltered,
                    hasActiveFilters: server.columnFilters.length > 0,
                })}
            />
        );
    };

    if (dealId === null) {
        return (
            <div data-testid="supporting-files-no-deal">
                <EmptyState
                    title="No submission"
                    description="This file is not part of a deal, so it has no supporting files."
                />
            </div>
        );
    }

    return (
        <div data-testid="supporting-files-tab" className="space-y-4">
            <div className="flex items-center justify-end">
                {canUpload && (
                    <Button data-testid="supporting-files-upload" onClick={onRequestUpload}>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                    </Button>
                )}
            </div>

            <Card className="overflow-hidden">
                <CardContent className="p-0">{renderContent()}</CardContent>
            </Card>

            <Pagination
                page={server.page}
                pageSize={PAGE_SIZE}
                total={filesState.data?.total ?? 0}
                onPageChange={server.setPage}
                className="mt-4"
            />

            <Modal
                open={fileToDelete !== null}
                onOpenChange={(open) => !open && closeDeleteModal()}
                title="Delete supporting file"
            >
                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Are you sure you want to delete{" "}
                        <strong>&ldquo;{fileToDelete?.friendlyName ?? ""}&rdquo;</strong>? This
                        action cannot be undone.
                    </p>

                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={closeDeleteModal}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            data-testid="supporting-file-delete-confirm"
                            onClick={handleConfirmDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <Loader size="sm" className="mr-2" />
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </Button>
                    </DialogFooter>
                </div>
            </Modal>
        </div>
    );
}
